import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "../lib/api-client";
import { Shield, Moon, Sun, LogOut, AlertTriangle, Lock, User, Mail, Users, Plus, Trash2, Edit2, KeyRound } from "lucide-react";
import { useAuthStore } from "../stores/auth-store";
import { useThemeStore } from "../stores/themeStore";
import { useFinanceStore } from "../stores/finance-store";
import { useNotificationStore } from "../stores/notification-store";
import { NotificationSettingsCard } from "../components/NotificationSettingsCard";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { PasswordInput } from "../components/PasswordInput";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { Separator } from "../components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { formatDate } from "../lib/utils";
import { decrypt, deriveKey, encrypt, generateVaultKey, getStoredKey, storeKey, wrapVaultKey, WRAPPED_KEY_MODES, wrapIdentifierFor } from "../lib/crypto";
import { toast } from "sonner";

const MEMBER_ROLES = [
  { value: "owner", label: "Owner" },
  { value: "admin", label: "Admin" },
  { value: "member", label: "Membro" },
];

const emptyMemberForm = {
  name: "",
  email: "",
  password: "",
  confirmPassword: "",
  role: "member",
};

const emptyPasswordForm = {
  password: "",
  confirmPassword: "",
};

const emptyMigrationForm = {
  password: "",
};

export default function SettingsPage() {
  const { user, activeTenant, logout, setActiveTenant, fetchTenants } = useAuthStore();
  const { theme, toggleTheme } = useThemeStore();
  const { clearData, fetchTransactions, fetchAccounts, fetchCategories, fetchCards } = useFinanceStore();
  const notificationConsent = useNotificationStore((state) => state.consent);
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [memberDialogOpen, setMemberDialogOpen] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [memberForm, setMemberForm] = useState(emptyMemberForm);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordMember, setPasswordMember] = useState(null);
  const [passwordForm, setPasswordForm] = useState(emptyPasswordForm);
  const [migrationDialogOpen, setMigrationDialogOpen] = useState(false);
  const [migrationForm, setMigrationForm] = useState(emptyMigrationForm);
  const [migrationLoading, setMigrationLoading] = useState(false);
  const canMigrateToWrappedKey = activeTenant && !WRAPPED_KEY_MODES.includes(activeTenant.crypto_mode);
  const tenantCryptoLabel = WRAPPED_KEY_MODES.includes(activeTenant?.crypto_mode)
    ? "uma senha só"
    : activeTenant?.crypto_mode === "legacy_user"
      ? "modo legado"
      : "senha do cofre";

  const getAuthHeaders = useCallback(() => (
    activeTenant?.id ? { headers: { "X-Tenant-ID": activeTenant.id } } : undefined
  ), [activeTenant?.id]);

  const fetchMembers = useCallback(async () => {
    if (!user) return;
    setMembersLoading(true);
    try {
      const response = await apiClient.get("/tenants/current/users", getAuthHeaders());
      setMembers(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao carregar usuários do cofre");
    } finally {
      setMembersLoading(false);
    }
  }, [getAuthHeaders, user]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  const handleLogout = async () => {
    clearData();
    await logout();
    navigate("/login");
  };

  const openCreateMember = () => {
    setEditingMember(null);
    setMemberForm(emptyMemberForm);
    setMemberDialogOpen(true);
  };

  const openEditMember = (member) => {
    setEditingMember(member);
    setMemberForm({
      name: member.name,
      email: member.email,
      password: "",
      confirmPassword: "",
      role: member.role,
    });
    setMemberDialogOpen(true);
  };

  const openPasswordDialog = (member) => {
    setPasswordMember(member);
    setPasswordForm(emptyPasswordForm);
    setPasswordDialogOpen(true);
  };

  const handlePasswordDialogChange = (open) => {
    setPasswordDialogOpen(open);
    if (!open) {
      setPasswordMember(null);
      setPasswordForm(emptyPasswordForm);
    }
  };

  const handleMigrationDialogChange = (open) => {
    setMigrationDialogOpen(open);
    if (!open) {
      setMigrationForm(emptyMigrationForm);
      setMigrationLoading(false);
    }
  };

  const handleMemberSubmit = async (event) => {
    event.preventDefault();
    try {
      if (editingMember) {
        await apiClient.put(
          `/tenants/current/users/${editingMember.id}`,
          { name: memberForm.name, role: memberForm.role },
          getAuthHeaders()
        );
        toast.success("Usuário atualizado!");
      } else {
        if (memberForm.password.length < 8) {
          toast.error("Senha deve ter pelo menos 8 caracteres");
          return;
        }
        if (memberForm.password !== memberForm.confirmPassword) {
          toast.error("As senhas não coincidem");
          return;
        }
        const { name, email, password, role } = memberForm;
        // Gerado no cliente pelo mesmo motivo do registro: o wrap precisa do id
        // antes do novo membro existir no servidor.
        const memberUserId = window.crypto.randomUUID();
        let wrapped_key;
        if (WRAPPED_KEY_MODES.includes(activeTenant?.crypto_mode)) {
          const vaultKey = getStoredKey();
          if (!vaultKey) {
            toast.error("Desbloqueie o cofre antes de criar usuários");
            return;
          }
          wrapped_key = await wrapVaultKey(vaultKey, password, wrapIdentifierFor(activeTenant.crypto_mode, { id: memberUserId, email }));
        }
        await apiClient.post("/tenants/current/users", { name, email, password, role, user_id: memberUserId, wrapped_key }, getAuthHeaders());
        toast.success("Usuário criado no cofre!");
      }
      setMemberDialogOpen(false);
      await fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao salvar usuário do cofre");
    }
  };

  const handleRemoveMember = async (member) => {
    try {
      await apiClient.delete(`/tenants/current/users/${member.id}`, getAuthHeaders());
      toast.success("Usuário removido do cofre!");
      await fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao remover usuário do cofre");
    }
  };

  const handlePasswordSubmit = async (event) => {
    event.preventDefault();
    if (!passwordMember) return;
    if (passwordForm.password.length < 8) {
      toast.error("Senha deve ter pelo menos 8 caracteres");
      return;
    }
    if (passwordForm.password !== passwordForm.confirmPassword) {
      toast.error("As senhas não coincidem");
      return;
    }

    try {
      let wrapped_key;
      if (WRAPPED_KEY_MODES.includes(activeTenant?.crypto_mode)) {
        const vaultKey = getStoredKey();
        if (!vaultKey) {
          toast.error("Desbloqueie o cofre antes de alterar a senha");
          return;
        }
        wrapped_key = await wrapVaultKey(vaultKey, passwordForm.password, wrapIdentifierFor(activeTenant.crypto_mode, passwordMember));
      }
      await apiClient.put(
        `/tenants/current/users/${passwordMember.id}/password`,
        { password: passwordForm.password, wrapped_key },
        getAuthHeaders()
      );
      toast.success("Senha alterada!");
      handlePasswordDialogChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao alterar senha");
    }
  };

  const migrateEncryptedItems = async (items, legacyKey, tenantKey) => Promise.all(
    items.map(async (item) => {
      const decrypted = await decrypt(item.encrypted_payload, item.iv, legacyKey);
      const encrypted = await encrypt(decrypted, tenantKey);
      return {
        id: item.id,
        encrypted_payload: encrypted.encrypted_payload,
        iv: encrypted.iv,
      };
    })
  );

  const handleMigrationSubmit = async (event) => {
    event.preventDefault();
    if (!canMigrateToWrappedKey) return;

    setMigrationLoading(true);
    try {
      const headers = getAuthHeaders();
      await apiClient.post("/auth/login", {
        email: user.email,
        password: migrationForm.password,
      });

      const sourceKey = activeTenant.crypto_mode === "legacy_user"
        ? await deriveKey(migrationForm.password, user.email)
        : getStoredKey();

      if (!sourceKey) {
        toast.error("Desbloqueie o cofre antes de migrar");
        return;
      }

      const tenantKey = await generateVaultKey();
      // Migração sempre cria o tenant no modo novo (wrapped_key_v2 no backend).
      const ownerWrappedKey = await wrapVaultKey(tenantKey, migrationForm.password, user.id);

      const [transactionsResponse, accountsResponse, categoriesResponse, cardsResponse] = await Promise.all([
        apiClient.get("/transactions", headers),
        apiClient.get("/accounts", headers),
        apiClient.get("/categories", headers),
        apiClient.get("/cards", headers),
      ]);

      const payload = {
        transactions: await migrateEncryptedItems(transactionsResponse.data, sourceKey, tenantKey),
        accounts: await migrateEncryptedItems(accountsResponse.data, sourceKey, tenantKey),
        categories: await migrateEncryptedItems(categoriesResponse.data, sourceKey, tenantKey),
        cards: await migrateEncryptedItems(cardsResponse.data, sourceKey, tenantKey),
        owner_wrapped_key: ownerWrappedKey,
      };

      const response = await apiClient.post("/tenants/current/crypto/migrate", payload, headers);
      storeKey(tenantKey, response.data.id);
      setActiveTenant(response.data);
      await fetchTenants();
      await Promise.all([fetchTransactions(), fetchAccounts(), fetchCategories(), fetchCards()]);
      toast.success("Cofre migrado para uma senha só!");
      handleMigrationDialogChange(false);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(detail || "Não foi possível migrar. Confira a senha de login e tente novamente.");
    } finally {
      setMigrationLoading(false);
    }
  };
  
  return (
    <div className="space-y-6 max-w-2xl">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Configurações</h1>
        <p className="text-muted-foreground mt-1">
          Gerencie sua conta e preferências
        </p>
      </div>
      
      {/* Profile */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5" />
            Perfil
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              <span className="text-primary text-2xl font-semibold">
                {user?.name?.charAt(0).toUpperCase()}
              </span>
            </div>
            <div>
              <p className="font-semibold text-lg">{user?.name}</p>
              <p className="text-muted-foreground flex items-center gap-1">
                <Mail className="w-4 h-4" />
                {user?.email}
              </p>
            </div>
          </div>
          
          <Separator />
          
          <div className="flex justify-between items-center">
            <div>
              <p className="text-sm font-medium">Conta criada em</p>
              <p className="text-sm text-muted-foreground">
                {user?.created_at ? formatDate(user.created_at) : "-"}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Vault Users */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Usuários do Cofre
              </CardTitle>
              <CardDescription>
                {activeTenant?.name || "Cofre atual"} · {tenantCryptoLabel}
              </CardDescription>
            </div>
            <Button size="sm" onClick={openCreateMember}>
              <Plus className="w-4 h-4 mr-2" />
              Novo
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {membersLoading ? (
            <p className="text-sm text-muted-foreground">Carregando usuários...</p>
          ) : members.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nenhum usuário vinculado.</p>
          ) : (
            members.map((member) => (
              <div key={member.id} className="flex flex-col gap-3 rounded-md border p-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <p className="font-medium truncate">{member.name}</p>
                  <p className="text-sm text-muted-foreground truncate">{member.email}</p>
                  <p className="text-xs text-primary mt-1">{MEMBER_ROLES.find((role) => role.value === member.role)?.label || member.role}</p>
                </div>
                <div className="flex gap-2 sm:justify-end">
                  <Button variant="outline" size="icon" aria-label="Editar usuário" title="Editar usuário" onClick={() => openEditMember(member)}>
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="icon" aria-label="Alterar senha" title="Alterar senha" onClick={() => openPasswordDialog(member)}>
                    <KeyRound className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="icon" aria-label="Remover usuário" title="Remover usuário" className="text-destructive hover:text-destructive" onClick={() => handleRemoveMember(member)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      {canMigrateToWrappedKey && (
        <Card className="border-secondary/50">
          <CardHeader>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <KeyRound className="w-5 h-5" />
                  Uma senha só
                </CardTitle>
                <CardDescription>
                  Migre este cofre para liberar a chave com a senha de login de cada usuário.
                </CardDescription>
              </div>
              <Button size="sm" onClick={() => setMigrationDialogOpen(true)}>
                Migrar para uma senha só
              </Button>
            </div>
          </CardHeader>
        </Card>
      )}
      
      {/* Security Info */}
      <Card className="border-secondary/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-secondary">
            <Shield className="w-5 h-5" />
            Segurança E2EE
          </CardTitle>
          <CardDescription>
            Seus dados são protegidos com criptografia end-to-end
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-start gap-3 p-3 bg-secondary/10 rounded-lg">
            <Lock className="w-5 h-5 text-secondary mt-0.5" />
            <div>
              <p className="font-medium">Criptografia AES-256-GCM</p>
              <p className="text-sm text-muted-foreground">
                Todos os seus dados financeiros são criptografados localmente antes de serem enviados ao servidor.
                A chave de criptografia é derivada da sua senha usando PBKDF2.
              </p>
            </div>
          </div>
          
          <div className="alert-banner alert-warning">
            <AlertTriangle className="w-5 h-5 flex-shrink-0" />
            <div>
              <p className="font-medium">Não há recuperação de senha</p>
              <p className="text-sm mt-1">
                Se você perder sua senha, todos os seus dados serão permanentemente inacessíveis.
                Recomendamos que você mantenha sua senha em um local seguro.
              </p>
            </div>
          </div>
          
          <div className="text-sm text-muted-foreground space-y-1">
            <p>
              • O servidor nunca tem acesso aos seus dados descriptografados
              {notificationConsent.grant_active
                ? " — exceto para compor suas notificações, conforme você autorizou. Revogável a qualquer momento em Notificações."
                : ""}
            </p>
            <p>• Sua senha nunca é armazenada ou transmitida</p>
            <p>• Todos os cálculos financeiros são feitos localmente</p>
          </div>
        </CardContent>
      </Card>
      
      {/* Notifications */}
      <NotificationSettingsCard />

      {/* Appearance */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {theme === "dark" ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
            Aparência
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="dark-mode">Modo Escuro</Label>
              <p className="text-sm text-muted-foreground">
                Ative o tema escuro para reduzir o cansaço visual
              </p>
            </div>
            <Switch
              id="dark-mode"
              checked={theme === "dark"}
              onCheckedChange={toggleTheme}
              data-testid="dark-mode-toggle"
            />
          </div>
        </CardContent>
      </Card>
      
      {/* Account Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Conta</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button
            variant="destructive"
            className="w-full"
            onClick={handleLogout}
            data-testid="settings-logout"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sair da Conta
          </Button>
          
          <p className="text-xs text-center text-muted-foreground">
            Ao sair, a chave de criptografia será removida da memória.
            Você precisará inserir sua senha novamente para acessar seus dados.
          </p>
        </CardContent>
      </Card>
      
      {/* Version */}
      <div className="text-center text-sm text-muted-foreground">
        <p>Cofre v1.0.0</p>
        <p className="text-xs">Controle financeiro com criptografia E2EE</p>
      </div>

      <Dialog open={memberDialogOpen} onOpenChange={setMemberDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingMember ? "Editar usuário do cofre" : "Novo usuário do cofre"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleMemberSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Nome</Label>
              <Input value={memberForm.name} onChange={(e) => setMemberForm({ ...memberForm, name: e.target.value })} required />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={memberForm.email}
                onChange={(e) => setMemberForm({ ...memberForm, email: e.target.value })}
                disabled={!!editingMember}
                required
              />
            </div>
            {!editingMember && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="member-password">Senha de Login</Label>
                  <PasswordInput
                    id="member-password"
                    value={memberForm.password}
                    onChange={(e) => setMemberForm({ ...memberForm, password: e.target.value })}
                    minLength={8}
                    required
                    data-testid="member-password"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="member-confirm-password">Confirmar Senha de Login</Label>
                  <PasswordInput
                    id="member-confirm-password"
                    value={memberForm.confirmPassword}
                    onChange={(e) => setMemberForm({ ...memberForm, confirmPassword: e.target.value })}
                    minLength={8}
                    required
                    data-testid="member-confirm-password"
                  />
                </div>
              </>
            )}
            <div className="space-y-2">
              <Label>Papel</Label>
              <Select value={memberForm.role} onValueChange={(role) => setMemberForm({ ...memberForm, role })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MEMBER_ROLES.map((role) => (
                    <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setMemberDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" className="flex-1 btn-glow">{editingMember ? "Atualizar" : "Criar"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={passwordDialogOpen} onOpenChange={handlePasswordDialogChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Alterar senha</DialogTitle>
          </DialogHeader>
          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="reset-member-password">Nova Senha</Label>
              <PasswordInput
                id="reset-member-password"
                value={passwordForm.password}
                onChange={(e) => setPasswordForm({ ...passwordForm, password: e.target.value })}
                minLength={8}
                required
                data-testid="reset-member-password"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="reset-member-confirm-password">Confirmar Senha</Label>
              <PasswordInput
                id="reset-member-confirm-password"
                value={passwordForm.confirmPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                minLength={8}
                required
                data-testid="reset-member-confirm-password"
              />
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => handlePasswordDialogChange(false)}>Cancelar</Button>
              <Button type="submit" className="flex-1 btn-glow">Salvar</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={migrationDialogOpen} onOpenChange={handleMigrationDialogChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Migrar para uma senha só</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleMigrationSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="migration-password">
                {activeTenant?.crypto_mode === "legacy_user" ? "Senha antiga da conta" : "Senha de login atual"}
              </Label>
              <PasswordInput
                id="migration-password"
                value={migrationForm.password}
                onChange={(e) => setMigrationForm({ ...migrationForm, password: e.target.value })}
                required
                data-testid="migration-password"
              />
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => handleMigrationDialogChange(false)} disabled={migrationLoading}>Cancelar</Button>
              <Button type="submit" className="flex-1 btn-glow" disabled={migrationLoading}>
                {migrationLoading ? "Migrando..." : "Migrar"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
